import { useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  DeleteUserUnidade,
  PatchUserIdFabrica,
  PostUsersUnidade,
} from "../../../../data/services";

type Unidade = {
  idGpUnidade: number;
  gpUnidadeDescricao: string;
};

type EditarColaboradorProps = {
  userData: {
    idUsuario: number;
    nomeUsuario: string;
    email: string;
    // agora aceitamos GP e TERCEIRO
    tipo: "GP" | "TERCEIRO";
    // recebe o login para exibir a matrícula
    login: string;
    unidades: Unidade[];
    unidadePrincipal: string | number;
    todasUnidades: Unidade[];
  };
  onCancel: () => void;
};

const EditarColaborador = ({ userData, onCancel }: EditarColaboradorProps) => {
  const unidadePrincipalId = (() => {
    if (typeof userData.unidadePrincipal === "number") {
      return userData.unidadePrincipal;
    }
    const principal = userData.unidades.find(
      (u) => u.gpUnidadeDescricao === userData.unidadePrincipal
    );
    return principal ? principal.idGpUnidade : "";
  })();

  const ApenasNumber = (value: string) => value.replace(/\D/g, "");

  const [form, setForm] = useState({
    idUsuario: userData.idUsuario,
    nomeUsuario: userData.nomeUsuario,
    email: userData.email,
    tipo: userData.tipo,
    // guarda como veio do backend (pode vir com ou sem C)
    login: userData.login ?? "",
    unidades: userData.unidades,
    unidadePrincipal: unidadePrincipalId,
  });

  const [selectedToAdd, setSelectedToAdd] = useState<string>("");
  const [loading, setLoading] = useState(false);

  // Como exibir a matrícula
  // - TERCEIRO: garante prefixo C + dígitos
  // - GP: só dígitos
  const loginDisplay =
    form.tipo === "TERCEIRO"
      ? (form.login || "").startsWith("C")
        ? form.login
        : `C${ApenasNumber(form.login)}`
      : ApenasNumber(form.login);

  const unidadesDisponiveis = userData.todasUnidades.filter(
    (u) => !form.unidades.some((sel) => sel.idGpUnidade === u.idGpUnidade)
  );

  const toggleUnidade = (unidade: Unidade) => {
    setForm((prev) => {
      const existe = prev.unidades.find(
        (u) => u.idGpUnidade === unidade.idGpUnidade
      );
      return {
        ...prev,
        unidades: existe
          ? prev.unidades.filter((u) => u.idGpUnidade !== unidade.idGpUnidade)
          : [...prev.unidades, unidade],
      };
    });
  };

  const handleAdicionarUnidades = async () => {
    if (!selectedToAdd) return;

    const unidadeSelecionada = unidadesDisponiveis.find(
      (u) => u.idGpUnidade.toString() === selectedToAdd
    );
    if (!unidadeSelecionada) return;

    setLoading(true);
    await PostUsersUnidade({ idUsuario: form.idUsuario, idUnidade: selectedToAdd });
    setLoading(false);

    setForm((prev) => ({
      ...prev,
      unidades: [...prev.unidades, unidadeSelecionada],
    }));
    setSelectedToAdd("");
    toast.success("Unidade adicionada com sucesso!");
  };

  const handleRemoverUnidade = async (idGpUnidade: number) => {
    setLoading(true);
    await DeleteUserUnidade(form.idUsuario, idGpUnidade);
    setLoading(false);

    setForm((prev) => ({
      ...prev,
      unidades: prev.unidades.filter((u) => u.idGpUnidade !== idGpUnidade),
      unidadePrincipal:
        prev.unidadePrincipal === idGpUnidade ? "" : prev.unidadePrincipal,
    }));
    toast.success("Unidade removida com sucesso!");
  };

  const handleSelecionarUnidadePrincipal = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const idGpUnidade = Number(e.target.value);
    if (!idGpUnidade) return;

    setLoading(true);
    await PatchUserIdFabrica(form.idUsuario, idGpUnidade);
    setForm((prev) => ({ ...prev, unidadePrincipal: idGpUnidade }));
    setLoading(false);
    toast.success("Unidade principal atualizada com sucesso!");
  };

  return (
    <Overlay>
      <ModalContainer>
        <Title>
          Editar Usuário {form.tipo === "TERCEIRO" ? "(Terceiro)" : "(Colaborador)"}
        </Title>

        <InputGroup>
          <Input
            value={form.nomeUsuario}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, nomeUsuario: e.target.value }))
            }
            placeholder="Nome do usuário"
            disabled
          />
          <Label>Nome</Label>
        </InputGroup>

        <InputGroup>
          <Input
            value={form.email}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, email: e.target.value }))
            }
            placeholder="E-mail"
            disabled
          />
          <Label>Email</Label>
        </InputGroup>

        {/* NOVO: Matrícula visível (somente leitura) */}
        <InputGroup>
          <Input
            value={loginDisplay}
            readOnly
            disabled
            placeholder="Matrícula"
          />
          <Label>Matrícula</Label>
        </InputGroup>

        <InputGroup>
          <Label>Adicionar Unidade</Label>
          <div style={{ display: "flex", gap: "8px" }}>
            <select
              value={selectedToAdd}
              onChange={(e) => setSelectedToAdd(e.target.value)}
              disabled={loading || unidadesDisponiveis.length === 0}
              style={{
                flex: 1,
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #cfd8dc",
                fontSize: "16px",
              }}
            >
              <option value="">Selecione uma unidade</option>
              {unidadesDisponiveis.map((u) => (
                <option key={u.idGpUnidade} value={u.idGpUnidade}>
                  {u.gpUnidadeDescricao}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAdicionarUnidades}
              disabled={!selectedToAdd || loading}
              style={{
                padding: "10px 16px",
                borderRadius: "6px",
                backgroundColor: "rgb(183, 152, 97)",
                color: "white",
                border: "none",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              Adicionar
            </button>
          </div>
        </InputGroup>

        {/* Unidades selecionadas */}
        {form.unidades.length > 0 && (
          <MultiSelectBox>
            {form.unidades.map((unidade) => (
              <UnidadeItem key={unidade.idGpUnidade} selected={true}>
                {unidade.gpUnidadeDescricao}
                <button
                  type="button"
                  onClick={() => handleRemoverUnidade(unidade.idGpUnidade)}
                  disabled={loading}
                >
                  x
                </button>
              </UnidadeItem>
            ))}
          </MultiSelectBox>
        )}

        <InputGroup>
          <Label>Unidade principal</Label>
          <select
            value={form.unidadePrincipal}
            onChange={handleSelecionarUnidadePrincipal}
            disabled={loading}
            style={{
              flex: 1,
              padding: "10px",
              borderRadius: "6px",
              border: "1px solid #cfd8dc",
              fontSize: "16px",
            }}
          >
            <option value="">Selecione</option>
            {form.unidades.map((u) => (
              <option key={u.idGpUnidade} value={u.idGpUnidade}>
                {u.gpUnidadeDescricao}
              </option>
            ))}
          </select>
        </InputGroup>

        <ButtonGroup>
          <ButtonCancelar onClick={onCancel} disabled={loading}>
            Voltar
          </ButtonCancelar>
        </ButtonGroup>
      </ModalContainer>
    </Overlay>
  );
};

export default EditarColaborador;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(20, 25, 41, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ModalContainer = styled.div`
  background: white;
  width: 500px;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.14);
`;

const Title = styled.h2`
  font-size: 22px;
  color: #2c3e50;
  margin-bottom: 30px;
  text-align: center;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 26px;
  margin-top: 33px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 1.8px solid #cfd8dc;
  border-radius: 6px;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #007bff;
  }
`;

const Label = styled.label`
  position: absolute;
  left: 14px;
  top: -10px;
  background: white;
  padding: 0 6px;
  font-size: 13px;
  color: #607d8b;
  pointer-events: none;
`;

const MultiSelectBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 20px 0;
  padding: 8px;
  border-radius: 6px;
  max-height: 190px;
  overflow-y: auto;
`;

const UnidadeItem = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  background: ${({ selected }) => (selected ? "#e0f7fa" : "#f5f5f5")};
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 14px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);

  button {
    background: transparent;
    border: none;
    color: red;
    font-weight: bold;
    margin-left: 10px;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    transition: color 0.2s ease;

    &:disabled {
      cursor: not-allowed;
      color: #ccc;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ButtonBase = styled.button`
  flex: 1;
  padding: 12px;
  font-size: 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  margin: 0 8px;
  border: none;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonCancelar = styled(ButtonBase)`
  background-color: #e0e0e0;
  color: #444;

  &:hover:not(:disabled) {
    background-color: #bdbdbd;
  }
`;
